import { Card, CardHeader, Button, Badge } from '@repo/ui-core'
import { useEffect, useState } from 'react'
import { chartOfAccountRepo } from '../../lib/db'
import type { ChartOfAccount, AccountType, NormalBalance } from '@repo/entity-accounting'
import { ACCOUNT_TYPE_LABELS, AccountingService } from '@repo/entity-accounting'
import { Plus, Sparkles, Search, CheckCircle, RefreshCw } from 'lucide-react'

export function ChartOfAccountsPage() {
  const [accounts, setAccounts] = useState<ChartOfAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedType, setSelectedType] = useState<string>('all')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)

  // Form State
  const [formData, setFormData] = useState<{
    code: string
    name: string
    accountType: AccountType
    normalBalance: NormalBalance
    parentCode: string
    description: string
    isHeader: boolean
  }>({
    code: '',
    name: '',
    accountType: 'asset',
    normalBalance: 'debit',
    parentCode: '',
    description: '',
    isHeader: false,
  })

  const loadAccounts = async () => {
    setLoading(true)
    try {
      const result = await chartOfAccountRepo.findMany({ page: 1, pageSize: 200 })
      if ('items' in result) {
        const sorted = (result.items as ChartOfAccount[]).sort((a, b) => a.code.localeCompare(b.code))
        setAccounts(sorted)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAccounts()
  }, [])

  const seedStandardCoa = async () => {
    setLoading(true)
    try {
      const defaultAccounts = AccountingService.getDefaultChartOfAccounts('default')
      for (const acc of defaultAccounts) {
        const existing = accounts.find(a => a.code === acc.code)
        if (!existing) {
          await chartOfAccountRepo.create({
            id: `coa-${acc.code}`,
            ...acc,
            createdAt: Date.now(),
            updatedAt: Date.now(),
            deletedAt: null,
            version: 1,
            createdBy: 'admin',
            updatedBy: 'admin',
          } as any)
        }
      }
      setStatusMessage('Standard Enterprise Chart of Accounts loaded successfully!')
      setTimeout(() => setStatusMessage(null), 4000)
      await loadAccounts()
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.code || !formData.name) return

    try {
      await chartOfAccountRepo.create({
        id: `coa-${formData.code}`,
        tenantId: 'default',
        code: formData.code,
        name: formData.name,
        accountType: formData.accountType,
        normalBalance: formData.normalBalance,
        parentCode: formData.parentCode || undefined,
        description: formData.description || undefined,
        isHeader: formData.isHeader,
        is_active: true,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        deletedAt: null,
        version: 1,
        createdBy: 'admin',
        updatedBy: 'admin',
      } as any)

      setIsModalOpen(false)
      setFormData({
        code: '',
        name: '',
        accountType: 'asset',
        normalBalance: 'debit',
        parentCode: '',
        description: '',
        isHeader: false,
      })
      await loadAccounts()
    } catch (err) {
      console.error(err)
    }
  }

  const accountTypeColors: Record<string, 'blue' | 'red' | 'green' | 'yellow' | 'purple'> = {
    asset: 'blue',
    liability: 'red',
    equity: 'green',
    income: 'yellow',
    expense: 'purple',
  }

  const filteredAccounts = accounts.filter(acc => {
    const matchesSearch =
      acc.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      acc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (acc.subtype && acc.subtype.toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesType = selectedType === 'all' || acc.accountType === selectedType
    return matchesSearch && matchesType
  })

  return (
    <div className="p-6 space-y-6">
      {statusMessage && (
        <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-emerald-600" />
          <span>{statusMessage}</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Chart of Accounts</h1>
          <p className="text-sm text-gray-500">QuickBooks & CDA compliant general ledger accounts hierarchy</p>
        </div>
        <div className="flex items-center gap-2">
          {accounts.length === 0 && (
            <Button
              variant="outline"
              onClick={seedStandardCoa}
              icon={<Sparkles className="h-4 w-4 text-indigo-600" />}
            >
              Seed Enterprise Template
            </Button>
          )}
          <Button
            variant="outline"
            onClick={loadAccounts}
            icon={<RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />}
          >
            Refresh
          </Button>
          <Button
            onClick={() => setIsModalOpen(true)}
            icon={<Plus className="h-4 w-4" />}
          >
            Add Account
          </Button>
        </div>
      </div>

      <Card>
        <div className="p-4 border-b border-gray-200 flex flex-col md:flex-row gap-4 justify-between items-center bg-gray-50/50">
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by code, name, or subtype..."
              className="w-full pl-9 pr-3 py-1.5 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto">
            {['all', 'asset', 'liability', 'equity', 'income', 'expense'].map(type => (
              <button
                key={type}
                onClick={() => setSelectedType(type)}
                className={`px-3 py-1 text-xs font-medium rounded-full capitalize transition-colors ${
                  selectedType === type
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {type === 'all' ? 'All Accounts' : ACCOUNT_TYPE_LABELS[type as AccountType] || type}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Account Code</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Account Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Subtype</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Normal Balance</th>
                <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredAccounts.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-400">
                    {accounts.length === 0 ? (
                      <div className="space-y-3">
                        <p>No accounts in the chart of accounts.</p>
                        <Button onClick={seedStandardCoa} icon={<Sparkles className="h-4 w-4" />}>
                          Load QuickBooks Enterprise Standard Accounts
                        </Button>
                      </div>
                    ) : (
                      'No matching accounts found.'
                    )}
                  </td>
                </tr>
              ) : (
                filteredAccounts.map(acc => (
                  <tr
                    key={acc.id || acc.code}
                    className={acc.isHeader ? 'bg-slate-50 font-bold text-gray-900' : 'hover:bg-gray-50'}
                  >
                    <td className="whitespace-nowrap px-4 py-2.5 text-sm font-mono text-gray-900">
                      {acc.code}
                    </td>
                    <td className={`whitespace-nowrap px-4 py-2.5 text-sm text-gray-900 ${acc.parentCode ? 'pl-8 text-gray-700' : ''}`}>
                      {acc.name}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5">
                      <Badge color={accountTypeColors[acc.accountType] ?? 'gray'} size="sm">
                        {ACCOUNT_TYPE_LABELS[acc.accountType] || acc.accountType}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs text-gray-500 capitalize">
                      {acc.subtype?.replace(/_/g, ' ') || '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-xs font-medium text-gray-600 capitalize">
                      {acc.normalBalance}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-center">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800">
                        Active
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add Account Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 space-y-4">
            <h2 className="text-lg font-bold text-gray-900">Add New Account</h2>
            <form onSubmit={handleCreateAccount} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Account Code *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. 1030"
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500"
                    value={formData.code}
                    onChange={e => setFormData({ ...formData, code: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Account Type *</label>
                  <select
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500"
                    value={formData.accountType}
                    onChange={e => {
                      const type = e.target.value as AccountType
                      const normalBal: NormalBalance = (type === 'asset' || type === 'expense') ? 'debit' : 'credit'
                      setFormData({ ...formData, accountType: type, normalBalance: normalBal })
                    }}
                  >
                    <option value="asset">Asset</option>
                    <option value="liability">Liability</option>
                    <option value="equity">Equity</option>
                    <option value="income">Income / Revenue</option>
                    <option value="expense">Expense</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Account Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. BDO Operating Account"
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Normal Balance</label>
                  <select
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500"
                    value={formData.normalBalance}
                    onChange={e => setFormData({ ...formData, normalBalance: e.target.value as NormalBalance })}
                  >
                    <option value="debit">Debit</option>
                    <option value="credit">Credit</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Parent Code (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. 1000"
                    className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500"
                    value={formData.parentCode}
                    onChange={e => setFormData({ ...formData, parentCode: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Description (Optional)</label>
                <textarea
                  rows={2}
                  placeholder="Brief notes regarding this account's purpose"
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500"
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isHeader"
                  checked={formData.isHeader}
                  onChange={e => setFormData({ ...formData, isHeader: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="isHeader" className="text-xs text-gray-700">
                  Header Account (summary group, cannot post direct transactions)
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
                <Button variant="outline" type="button" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  Save Account
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
